/**
 * Modal "Reportar problema" de un recurso físico — Sprint 18 P8.
 *
 * El vecino abre desde /vecino/mis-datos (sección "Recursos asignados")
 * sobre un recurso entregado. Reporta pérdida / robo / daño / defecto.
 *
 * Submit → POST /me/recursos/{id}/reportar. El recurso queda `reportado`
 * y se crea una solicitud de reposición que procesa el tesorero. Si el
 * motivo es "defecto" (falla de fábrica) y el recurso fue entregado hace
 * menos de la ventana de garantía, la reposición es sin costo (P9); el
 * tesorero lo confirma al aprobar.
 */
import { useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  reportarProblemaRecurso,
  type MotivoReporte,
} from '../../lib/api/solicitudes-recurso';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface Props {
  recurso: {
    id: string;
    tipo: string;
    identificador: string;
    residente_actual_id: string | null;
  };
  etiquetaTipo: string;
  onCerrar: () => void;
  onReportado: () => void;
}

const MOTIVOS: { value: MotivoReporte; label: string }[] = [
  { value: 'perdida', label: 'Lo perdí' },
  { value: 'robo', label: 'Me lo robaron' },
  { value: 'deterioro', label: 'Está dañado' },
  { value: 'falla_fabrica', label: 'Tiene un defecto de fábrica' },
];

export function ModalReportarProblema({
  recurso,
  etiquetaTipo,
  onCerrar,
  onReportado,
}: Props) {
  const [motivo, setMotivo] = useState<MotivoReporte>('perdida');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await reportarProblemaRecurso(recurso.id, {
        motivo,
        motivo_texto: motivoTexto.trim() || null,
        residente_id: recurso.residente_actual_id,
      });
      onReportado();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.body === 'object' &&
            err.body !== null &&
            'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`,
        );
      } else {
        setError('Error de red al reportar.');
      }
      setEnviando(false);
    }
  }

  return (
    <Modal
      title="Reportar un problema"
      onClose={onCerrar}
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar reporte'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary mb-3">
        {etiquetaTipo}{' '}
        <span className="font-mono text-text-muted">{recurso.identificador}</span>
      </p>

      <label className="block text-sm text-text-secondary mb-1">
        ¿Qué pasó?
      </label>
      <div className="space-y-1.5 mb-3">
        {MOTIVOS.map((m) => (
          <label
            key={m.value}
            className="flex items-center gap-2 text-sm text-text-primary"
          >
            <input
              type="radio"
              name="motivo"
              checked={motivo === m.value}
              onChange={() => setMotivo(m.value)}
            />
            {m.label}
          </label>
        ))}
      </div>

      {motivo === 'robo' && (
        <div className="bg-state-warning/15 border-l-4 border-state-warning p-2.5 rounded-sm text-xs text-text-primary mb-3">
          Recuerda hacer la denuncia en Carabineros. El robo de un control
          o llavero es un riesgo de seguridad para todo el barrio.
        </div>
      )}
      {motivo === 'falla_fabrica' && (
        <div className="bg-state-info/15 border-l-4 border-state-info p-2.5 rounded-sm text-xs text-text-primary mb-3">
          Si te lo entregamos hace poco (dentro del plazo de garantía), el
          reemplazo es <strong>sin costo</strong>. La Comisión lo verifica al
          revisar tu reporte.
        </div>
      )}

      <label className="block text-sm text-text-secondary mb-1">
        Cuéntanos más (opcional)
      </label>
      <textarea
        value={motivoTexto}
        onChange={(e) => setMotivoTexto(e.target.value)}
        rows={3}
        className="w-full bg-bg-card border-[0.5px] border-line-strong text-text-primary rounded-sm px-3 py-2 text-sm"
        placeholder="Ej: se cayó al estacionamiento y dejó de funcionar"
      />

      {error && (
        <p className="text-sm text-state-danger mt-3" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
